import { forwardRef, useRef } from 'react'
import type { ButtonHTMLAttributes, PointerEvent, ReactNode } from 'react'
import { emitZap } from './zapRipple'

export interface FabProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  icon: ReactNode
  label: string
  extended?: boolean
}

export const Fab = forwardRef<HTMLButtonElement, FabProps>(function Fab(
  {
    icon,
    label,
    extended = false,
    disabled = false,
    className,
    children,
    onPointerDown,
    ...rest
  },
  ref,
) {
  const localRef = useRef<HTMLButtonElement | null>(null)

  const handlePointerDown = (event: PointerEvent<HTMLButtonElement>) => {
    onPointerDown?.(event)
    if (event.defaultPrevented || disabled) return
    const el = event.currentTarget
    const rect = el.getBoundingClientRect()
    emitZap(el, event.clientX - rect.left, event.clientY - rect.top)
  }

  const classes = [
    'zapBtn',
    'zapBtn--primary',
    'zapBtn--fab',
    extended ? 'zapBtn--fabExtended' : '',
    className ?? '',
  ]
    .filter(Boolean)
    .join(' ')

  const hasChildren = children !== undefined && children !== null && children !== false && children !== ''

  return (
    <button
      ref={(node) => {
        localRef.current = node
        if (typeof ref === 'function') ref(node)
        else if (ref) (ref as { current: HTMLButtonElement | null }).current = node
      }}
      type={rest.type ?? 'button'}
      className={classes}
      disabled={disabled}
      aria-label={extended && !hasChildren ? rest['aria-label'] : (rest['aria-label'] ?? label)}
      onPointerDown={handlePointerDown}
      {...rest}
    >
      <span className="zapBtn__glow" aria-hidden="true" />
      <span className="zapBtn__charge" aria-hidden="true" />
      <span className={extended ? 'zapBtn__icon zapBtn__icon--lead' : 'zapBtn__icon'} aria-hidden="true">
        {icon}
      </span>
      {extended && !hasChildren && <span className="zapBtn__label">{label}</span>}
      {children}
    </button>
  )
})
